const identityWords = (value) => String(value || "")
  .normalize("NFKD").replace(/\p{M}/gu, "").toLowerCase()
  .replace(/[^a-z0-9]+/g, " ").trim().split(/\s+/).filter(Boolean);

export function matchLearnerFromIdentity(learners = [], { learnerName = "", learnerStudentNumber = "", fileName = "", pageText = "" } = {}) {
  if (!Array.isArray(learners) || !learners.length) return null;
  const fullNameMatches = (learner, source) => {
    const first = identityWords(learner.first_name);
    const last = identityWords(learner.last_name);
    const words = identityWords(source);
    return first.length > 0 && last.length > 0 && [...first, ...last].every(word => words.includes(word));
  };
  const cleanNum = String(learnerStudentNumber || "").trim().toLowerCase().replace(/[^a-z0-9]/g, "");
  if (cleanNum.length >= 3) {
    const numbered = learners.filter(learner => String(learner.student_number || "").trim().toLowerCase().replace(/[^a-z0-9]/g, "") === cleanNum);
    if (numbered.length === 1) {
      return !String(learnerName).trim() || fullNameMatches(numbered[0], learnerName) ? numbered[0] : null;
    }
  }
  const sources = String(learnerName || "").trim() ? [learnerName] : [pageText, String(fileName).replace(/\.[^.]+$/, "")];
  for (const source of sources) {
    const matches = learners.filter(learner => fullNameMatches(learner, source));
    if (matches.length === 1) return matches[0];
    if (matches.length > 1) return null;
  }
  return null;
}

export function gradingSubjectMismatch(detected, selected) {
  const family = value => {
    if (/math|wiskund/i.test(value || "")) return "math";
    if (/afrikaans/i.test(value || "")) return "afrikaans";
    if (/english|engels/i.test(value || "")) return "english";
    return "";
  };
  const actual = family(detected);
  const target = family(selected);
  return Boolean(actual && target && actual !== target);
}

export function calculateMarkingTotals(results = []) {
  const sectionTotals = Object.create(null);
  const finiteMark = value => Number.isFinite(Number(value)) ? Math.max(0, Number(value)) : 0;
  const clean = value => Number(value.toFixed(10));
  for (const result of results) {
    const section = String(result.section || "").trim() || "Other";
    const max = finiteMark(result.maxMarks);
    const awarded = Math.min(max, finiteMark(result.marksAwarded));
    if (!sectionTotals[section]) sectionTotals[section] = { awarded: 0, max: 0 };
    sectionTotals[section].awarded = clean(sectionTotals[section].awarded + awarded);
    sectionTotals[section].max = clean(sectionTotals[section].max + max);
  }
  const sections = Object.values(sectionTotals);
  const totalAwarded = clean(sections.reduce((sum, section) => sum + section.awarded, 0));
  const totalMarks = clean(sections.reduce((sum, section) => sum + section.max, 0));
  return { sectionTotals, total: totalAwarded, totalAwarded, totalMarks };
}

export function findLearnerPageRanges(pageTexts = []) {
  const starts = [];
  let previous = "";
  pageTexts.forEach((text, index) => {
    const match = String(text).match(/\b(?:LEERDER|LEARNER)\s*:?\s*([\p{L}\p{M}'’.-]+(?:\s+[\p{L}\p{M}'’.-]+){1,5}?)\s+(?:SKOOL|SCHOOL)\b/iu);
    if (!match) return;
    const identity = identityWords(match[1]).join(" ");
    if (identity !== previous) { starts.push(index); previous = identity; }
  });
  if (starts.length < 2 || starts[0] !== 0) return null;
  return starts.map((startPage, index) => ({ startPage, endPage: starts[index + 1] ?? pageTexts.length }));
}

export function parseMarkingPayload(rawData) {
  let payload = rawData;
  if (typeof payload === "string") {
    const trimmed = payload.trim();
    try {
      payload = JSON.parse(trimmed);
    } catch {
      const firstBrace = trimmed.indexOf("{");
      const lastBrace = trimmed.lastIndexOf("}");
      if (firstBrace >= 0 && lastBrace > firstBrace) {
        try {
          payload = JSON.parse(trimmed.slice(firstBrace, lastBrace + 1));
        } catch {
          payload = null;
        }
      }
    }
  }

  if (!payload || typeof payload !== "object") {
    throw new Error(
      "The server returned an empty or unreadable marking response. Please try again.",
    );
  }

  if (typeof payload.error === "string" && payload.error.trim() !== "") {
    throw new Error(payload.error.trim());
  }
  if (payload.skipped) return payload;

  let rawResults = null;
  if (Array.isArray(payload.results) && payload.results.length > 0) {
    rawResults = payload.results;
  } else if (Array.isArray(payload.evals) && payload.evals.length > 0) {
    const itemMap = {};
    const rawItems = Array.isArray(payload.items)
      ? payload.items
      : Array.isArray(payload.t)
        ? payload.t
        : [];
    rawItems.forEach((item) => {
      if (item && item.q) {
        const key = String(item.q).trim().toLowerCase().replace(/[^a-z0-9]/g, "");
        itemMap[key] = item;
      }
    });

    rawResults = payload.evals.map((e, idx) => {
      const qNum = String(e?.q || `${idx + 1}`);
      const normKey = qNum.trim().toLowerCase().replace(/[^a-z0-9]/g, "");
      const matchedItem = itemMap[normKey] || {};
      const answer =
        e?.a !== undefined
          ? String(e.a)
          : String(matchedItem?.a || "");
      const maxMarks = Math.max(0, Number(e?.maxMarks ?? matchedItem?.max ?? 0) || 0);
      const marks = Math.min(maxMarks, Math.max(0, Number(e?.marks ?? 0) || 0));
      const err = String(e?.err || "NONE").toUpperCase();
      const hasError = err !== "NONE";
      const pass =
        e?.pass !== undefined ? Boolean(e.pass) : !hasError && marks > 0;
      const feedback = String(e?.note || e?.rsn || e?.feedback || "");

      return {
        questionNumber: qNum,
        section: String(
          e?.section ||
            (qNum.match(/^\d+/) ? `VRAAG ${qNum.match(/^\d+/)[0]}` : ""),
        ),
        learnerAnswer: answer || "[blank]",
        correctAnswer: String(e?.correctAnswer || ""),
        marksAwarded: marks,
        maxMarks: maxMarks,
        confidence: null,
        confidenceSource: "not_provided",
        needsReview: !pass || hasError || marks === 0,
        feedback: feedback,
        err: err,
        evidencePage: 1,
        evidenceText: answer !== "[blank]" ? answer : "",
      };
    });
  } else if (Array.isArray(payload.questions)) {
    rawResults = payload.questions;
  }

  if (!rawResults || rawResults.length === 0) {
    throw new Error(
      "The AI could not extract question results from this document. Please check that the scan and memo are clear and try again.",
    );
  }

  const normalizedResults = rawResults.map((item, idx) => ({
    kind: ['essay', 'transactional', 'summary'].includes(item?.kind) ? item.kind : 'question',
    is_rubric_task: Boolean(item?.is_rubric_task),
    writingRubric: item?.writingRubric || null,
    writingEvidence: item?.writingEvidence || null,
    inline_annotations: Array.isArray(item?.inline_annotations) ? item.inline_annotations : [],
    section: String(item?.section || ""),
    questionNumber: String(item?.questionNumber || `${idx + 1}`),
    learnerAnswer: String(item?.learnerAnswer ?? ""),
    correctAnswer: String(item?.correctAnswer ?? ""),
    evidencePage: Math.max(0, Number(item?.evidencePage) || 0),
    evidenceText: String(item?.evidenceText ?? ""),
    answerBox: item?.answerBox || null,
    lineEvals: Array.isArray(item?.lineEvals) ? item.lineEvals : [],
    criterionEvals: Array.isArray(item?.criterionEvals) ? item.criterionEvals : [],
    transcriptionSource: String(item?.transcriptionSource || "vision"),
    copyCheck: item?.copyCheck || null,
    marksAwarded: Math.min(Math.max(0, Number(item?.maxMarks) || 0), Math.max(0, Number(item?.marksAwarded) || 0)),
    maxMarks: Math.max(0, Number(item?.maxMarks) || 0),
    confidence: item?.confidenceSource !== "not_provided" && Number(item?.confidence) > 0 && Number(item?.confidence) <= 1
      ? Number(item.confidence) : null,
    confidenceSource: item?.confidenceSource || (Number(item?.confidence) > 0 ? "model_reported" : "not_provided"),
    needsReview: Boolean(item?.needsReview),
    reviewReason: String(item?.reviewReason || ""),
    autoChecked: Boolean(item?.autoChecked),
    methodSteps: Array.isArray(item?.methodSteps) ? item.methodSteps : [],
    feedback: String(item?.feedback ?? ""),
    err: String(item?.err || "NONE"),
  }));

  const totals = calculateMarkingTotals(normalizedResults);
  return {
    ...payload,
    ...totals,
    totalMarks: Number(payload.totalMarks) > 0
      ? Number(payload.totalMarks)
      : totals.totalMarks,
    learnerName: String(payload.learnerName || payload.learner || ""),
    school: String(payload.school || ""),
    subjectName: String(payload.subjectName || payload.subject || ""),
    learner: String(payload.learner || payload.learnerName || ""),
    subject: String(payload.subject || payload.subjectName || ""),
    learnerStudentNumber: String(payload.learnerStudentNumber || ""),
    results: normalizedResults,
    suggestedAnnotations: Array.isArray(payload.suggestedAnnotations)
      ? payload.suggestedAnnotations
      : [],
  };
}

